import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Building2, Users, CheckCircle2, AlertCircle, Loader2, ArrowRight, Github, Brain, Sparkles } from 'lucide-react';
import { useAuth } from '../auth';
import Button from '../ui/Button';
import { useAnalytics } from '../../services/analytics';
import {
  getInviteLinkDetails,
  getInviteDetails,
  acceptInviteLink,
  acceptInvite,
  listOrganizations,
  getOrCreatePersonalOrg,
} from '../../services/organizationsApi';

const GETTING_STARTED_STEPS = [
  { step: '1', label: 'Import', desc: 'Connect GitHub and select repositories', icon: Github },
  { step: '2', label: 'Analyze', desc: 'AI models cross-validate your code patterns', icon: Brain },
  { step: '3', label: 'Discover', desc: 'View skills, valuations, and recommendations', icon: Sparkles },
];

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const fetchInvite = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await getInviteLinkDetails(token);
      setInvite(details);
    } catch {
      try {
        const details = await getInviteDetails(token);
        setInvite(details);
      } catch {
        setError('This invite link is invalid or has expired.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvite();
  }, [fetchInvite]);

  useEffect(() => {
    if (!user && token) {
      sessionStorage.setItem('pendingInviteToken', token);
    }
  }, [user, token]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      try {
        await acceptInviteLink(user, token);
      } catch {
        await acceptInvite(user, token);
      }

      // Auto-create workspace if user has none
      try {
        const orgs = await listOrganizations(user);
        if (!orgs || orgs.length === 0) {
          await getOrCreatePersonalOrg(user);
        }
      } catch {
        // Non-critical — user can create workspace later
      }

      trackEvent('invite_accepted', {
        organization: invite?.organization_name || 'unknown',
        role: invite?.role || 'member',
      });

      setAccepted(true);
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch {
      setError('Failed to accept the invite. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-white/60">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Loading invite details...</p>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-error-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            Invalid Invite
          </h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Button as={Link} to="/login" variant="secondary">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-8 max-w-md w-full">
        {accepted ? (
          <div>
            <div className="text-center mb-6">
              <CheckCircle2 className="w-12 h-12 text-success-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-1">
                Welcome to {invite?.organization_name || 'Portfolistic'}!
              </h2>
              <p className="text-white/60 text-sm">
                You&apos;re in as <span className="text-emerald-400 capitalize">{invite?.role || 'member'}</span>. Here&apos;s how to get started:
              </p>
            </div>

            <div className="space-y-2 mb-6">
              {GETTING_STARTED_STEPS.map((item) => (
                <div
                  key={item.step}
                  className="flex items-start gap-3 p-3 rounded-xl border border-white/[0.06]"
                >
                  <div className="w-7 h-7 rounded-lg bg-emerald-400/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm">{item.label}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-white/40 text-xs">
              Redirecting to dashboard...
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-xl bg-primary-500/20 border border-primary-500/30 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-7 h-7 text-primary-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-1">
                You&apos;ve been invited
              </h2>
              <p className="text-white/60 text-sm">
                to join a workspace on Portfolistic
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-4 mb-6 space-y-3">
              {invite?.organization_name && (
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-white/40 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Workspace</p>
                    <p className="text-white font-medium">
                      {invite.organization_name}
                    </p>
                  </div>
                </div>
              )}
              {invite?.role && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-white/40 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Role</p>
                    <p className="text-white font-medium capitalize">
                      {invite.role}
                    </p>
                  </div>
                </div>
              )}
              {invite?.invited_by && (
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-white/40 shrink-0" />
                  <div>
                    <p className="text-xs text-white/40">Invited by</p>
                    <p className="text-white font-medium">
                      {invite.invited_by}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-error-400 text-sm mb-4 bg-error-500/10 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {user ? (
              <Button
                onClick={handleAccept}
                loading={accepting}
                loadingText="Accepting..."
                fullWidth
                icon={ArrowRight}
                iconPosition="right"
              >
                Accept Invite
              </Button>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-white/60 text-sm">
                  Sign up or log in to accept this invite.
                </p>
                <Button
                  as={Link}
                  to="/login"
                  fullWidth
                  icon={ArrowRight}
                  iconPosition="right"
                >
                  Continue to Login
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
